import { Flex, HStack, Text, VStack } from '@chakra-ui/layout';
import { useMemo } from 'react';
import RevisionAssessmentCarousel from '../RevisionAssessmentCarousel';
import type { AssessmentInRevision } from '@duke-hq/svc-api/lib/assessment/types';

export interface AssessmentsInRevisionLabels {
  title: string;
}

const defaultLabels: AssessmentsInRevisionLabels = {
  title: 'Assessments in revision',
};

export type AssessmentsInRevisionProps = {
  assessmentsInRevision: AssessmentInRevision[];
  labels?: Partial<AssessmentsInRevisionLabels>;
};

function AssessmentsInRevision({
  assessmentsInRevision,
  labels,
}: AssessmentsInRevisionProps): React.ReactElement {
  const l = { ...defaultLabels, ...labels };

  const groupedAssessments = useMemo(() => {
    const groups: (typeof assessmentsInRevision)[] = [];
    if (!Array.isArray(assessmentsInRevision) || assessmentsInRevision?.length === 0) {
      return groups;
    }
    for (let i = 0; i < assessmentsInRevision?.length; i += 2) {
      groups.push(assessmentsInRevision.slice(i, i + 2));
    }
    return groups;
  }, [assessmentsInRevision]);

  return (
    <Flex
      backgroundColor="bg.surface"
      rounded={6}
      borderWidth={1}
      borderColor="border.default"
      flexDirection="column"
      w="100%"
      p={3}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="medium">{l.title}</Text>
        <Text>{assessmentsInRevision?.length}</Text>
      </Flex>
      <RevisionAssessmentCarousel>
        {groupedAssessments?.map((group, index) => (
          <VStack key={index} w="full" gap={1} justifyContent="center" h="full">
            {group?.map((assessment) => (
              <HStack
                key={assessment.unitOfCompetency.code}
                justifyContent="space-between"
                w="full"
              >
                <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
                  {assessment.unitOfCompetency.code}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {assessment.correctAnswers}/{assessment.totalAnswers}{' '}
                  {groupedAssessments?.length === 1 ? 'Complete' : ''}
                </Text>
              </HStack>
            ))}
          </VStack>
        ))}
      </RevisionAssessmentCarousel>
    </Flex>
  );
}

export default AssessmentsInRevision;
